import { RefreshCcw, Sliders, PanelRightClose } from 'lucide-react';
import { useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Parameter } from '@shared/types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ParameterInput } from '@/components/parameter/ParameterInput';
import { ColorPicker } from '@/components/parameter/ColorPicker';
import { validateParameterValue } from '@/utils/parameterUtils';
import { useCurrentMessage } from '@/contexts/CurrentMessageContext';
import { useChangeParameters } from '@/services/messageService';

interface ParameterSectionProps {
  onClose?: () => void;
}

export function ParameterSection({ onClose }: ParameterSectionProps) {
  const changeParameters = useChangeParameters();
  const { currentMessage } = useCurrentMessage();
  const parameters = currentMessage?.content.artifact?.parameters ?? [];

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingParametersRef = useRef<Parameter[] | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const debouncedSubmit = useCallback(
    (params: Parameter[]) => {
      pendingParametersRef.current = params;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (pendingParametersRef.current) {
          changeParameters(currentMessage, pendingParametersRef.current);
          pendingParametersRef.current = null;
        }
      }, 200);
    },
    [changeParameters, currentMessage],
  );

  const handleCommit = (param: Parameter, value: Parameter['value']) => {
    const validatedValue = validateParameterValue(param, value);

    const updatedParam = { ...param, value: validatedValue };
    const updatedParameters = parameters.map((p) =>
      p.name === param.name ? updatedParam : p,
    );

    debouncedSubmit(updatedParameters);
  };

  return (
    <div className="h-full w-full max-w-full border-l border-dc-border-secondary bg-dc-bg-secondary">
      {/* Header */}
      <div className="flex h-14 items-center justify-between border-b border-dc-border-secondary bg-dc-bg-tertiary/50 px-4 py-4 pt-5">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-dc-accent-primary/20 to-dc-accent-secondary/20">
            <Sliders className="h-4 w-4 text-dc-accent-primary" />
          </div>
          <span className="text-base font-semibold text-dc-text-primary">
            Parameters
          </span>
        </div>
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 w-8 rounded-lg p-0 text-dc-text-tertiary transition-colors hover:bg-dc-bg-hover hover:text-dc-text-primary"
                  disabled={parameters.length === 0}
                  onClick={() => {
                    const newParameters = parameters.map((param) => ({
                      ...param,
                      value: param.defaultValue,
                    }));
                    changeParameters(currentMessage, newParameters);
                  }}
                >
                  <RefreshCcw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="border-dc-border-primary bg-dc-bg-elevated">
                <p className="text-dc-text-primary">Reset all parameters</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {onClose && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-8 w-8 rounded-lg p-0 text-dc-text-tertiary transition-colors hover:bg-dc-bg-hover hover:text-dc-text-primary"
                    onClick={onClose}
                  >
                    <PanelRightClose className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="border-dc-border-primary bg-dc-bg-elevated">
                  <p className="text-dc-text-primary">Collapse panel</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex h-[calc(100%-3.5rem)] flex-col justify-between overflow-hidden">
        <ScrollArea className="flex-1 px-6 py-4">
          <div className="flex flex-col gap-4">
            {parameters.map((param) => (
              <ParameterInput
                key={param.name}
                param={param}
                handleCommit={handleCommit}
              />
            ))}
          </div>
        </ScrollArea>

        {/* Footer with color picker */}
        <div className="border-t border-dc-border-secondary bg-dc-bg-tertiary/30 px-6 py-5">
          <ColorPicker />
        </div>
      </div>
    </div>
  );
}
